import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import {
  triggerInstallPrompt,
  useInstallReady,
  isStandalone as detectStandalone,
  isIOS as detectIos,
} from "../lib/installPrompt";

/**
 * "Install LAST" button — always-visible install trigger.
 *
 * Behaviour:
 *   - Chromium (Android/desktop Chrome, Edge, Brave) → fires the captured
 *     `beforeinstallprompt` event so the native install dialog appears.
 *   - iOS Safari → opens an instructional modal (Apple doesn't expose an
 *     install API to web pages).
 *   - Anything else (Firefox, desktop Safari, in-app browsers) → opens a
 *     generic "use your browser's menu" modal so the button never silently
 *     does nothing.
 *
 * The component hides itself only in two cases:
 *   - The app is already running standalone (already installed), or
 *   - The admin has turned the toggle off (`disabled` prop = true).
 */
export function InstallButton({ className = "", testId = "install-button", disabled = false, compact = false, large = false }) {
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [helpKind, setHelpKind] = useState(null); // 'ios' | 'generic' | null
  const installReady = useInstallReady();

  useEffect(() => {
    setIsStandalone(detectStandalone());
    setIsIos(detectIos());
    // The actual analytics ping now lives in lib/installPrompt.js's own
    // single module-level `appinstalled` listener (see comment there) —
    // this component only needs to flip its own visibility off.
    const onInstalled = () => setIsStandalone(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  if (disabled) return null;
  if (isStandalone) return null;

  const handleClick = async () => {
    if (installReady) {
      const outcome = await triggerInstallPrompt();
      if (outcome === "accepted") setIsStandalone(true);
      return;
    }
    if (isIos) {
      setHelpKind("ios");
      return;
    }
    // No native install event and not iOS — show the generic help modal so
    // the click always does *something* visible to the user.
    setHelpKind("generic");
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid={testId}
        className={
          compact
            ? `inline-flex items-center justify-center rounded-full border border-black/10 bg-white text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white sm:h-10 sm:w-10 ${
                large ? "h-11 w-11" : "h-6 w-6"
              } ${className}`
            : `inline-flex items-center gap-1.5 rounded-md border border-[#0A0F17] bg-white px-3 py-2 font-display text-sm font-bold text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white ${className}`
        }
        aria-label="Install LAST as an app"
        title={compact ? "Install LAST" : undefined}
      >
        <Download size={compact ? (large ? 20 : 16) : 14} strokeWidth={2.5} />
        {compact ? null : <> Install LAST</>}
      </button>

      {helpKind === "ios" ? (
        <IosInstallModal onClose={() => setHelpKind(null)} />
      ) : null}
      {helpKind === "generic" ? (
        <GenericInstallModal onClose={() => setHelpKind(null)} />
      ) : null}
    </>
  );
}

export function IosInstallModal({ onClose }) {
  return (
    <ModalShell
      kicker="Install on iOS"
      title="Add LAST to your home screen."
      testId="ios-install-modal"
      onClose={onClose}
    >
      <ol className="mt-5 space-y-4 text-sm text-[#0A0F17]">
        <Step n={1}>
          Tap the <span className="font-semibold">Share</span> icon{" "}
          <Share size={14} className="inline-block align-middle" /> at the
          bottom of Safari.
        </Step>
        <Step n={2}>
          Scroll the share sheet and tap{" "}
          <span className="font-semibold">Add to Home Screen</span>{" "}
          <Plus size={14} className="inline-block align-middle" />.
        </Step>
        <Step n={3}>
          Tap <span className="font-semibold">Add</span> in the top-right —
          and you're done. LAST will live on your home screen like any
          other app.
        </Step>
      </ol>
      <CloseButton onClose={onClose} testId="ios-install-modal-got-it" />
    </ModalShell>
  );
}

export function GenericInstallModal({ onClose }) {
  return (
    <ModalShell
      kicker="Install LAST"
      title="Add LAST to your home screen."
      testId="generic-install-modal"
      onClose={onClose}
    >
      <p className="mt-4 text-sm leading-relaxed text-[#475569]">
        Your browser doesn't support one-tap install just yet. Use the menu
        below to add LAST to your phone or desktop:
      </p>
      <ul className="mt-4 space-y-3 text-sm text-[#0A0F17]">
        <li className="flex items-start gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#6CABDD] text-xs font-black text-white">
            ⋮
          </span>
          <span className="flex-1">
            <span className="font-semibold">Chrome / Edge / Brave:</span>{" "}
            tap the three-dot menu (top-right), then{" "}
            <span className="font-semibold">Install app</span> or{" "}
            <span className="font-semibold">Add to Home Screen</span>.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#6CABDD] text-xs font-black text-white">
            ☰
          </span>
          <span className="flex-1">
            <span className="font-semibold">Firefox (Android):</span> tap
            the three-dot menu and choose{" "}
            <span className="font-semibold">Install</span>.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#6CABDD] text-xs font-black text-white">
            <Share size={12} />
          </span>
          <span className="flex-1">
            <span className="font-semibold">Safari (Mac):</span> File →{" "}
            <span className="font-semibold">Add to Dock</span>.
          </span>
        </li>
      </ul>
      <p className="mt-4 text-xs text-[var(--brand-body)]">
        If the option's missing, your browser may not support installable web
        apps — try Chrome or Edge instead.
      </p>
      <CloseButton onClose={onClose} testId="generic-install-modal-got-it" />
    </ModalShell>
  );
}

function ModalShell({ kicker, title, testId, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/60 px-4 py-6 sm:items-center"
      role="dialog"
      aria-modal="true"
      data-testid={testId}
      onClick={onClose}
      style={{
        // Use dynamic viewport height so iOS Safari's address bar
        // doesn't push the modal off the visible area when the keyboard
        // is closed, and respect the home-bar safe area at the bottom.
        minHeight: "100dvh",
        paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))",
        paddingTop: "max(1.5rem, env(safe-area-inset-top))",
      }}
    >
      <div
        className="my-auto w-full max-w-sm rounded-lg bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="label-kicker text-brand-accent">{kicker}</p>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tight text-[#0A0F17]">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testId}-close`}
            aria-label="Close install instructions"
            className="rounded-full p-1 text-[var(--brand-body)] hover:bg-black/5"
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#6CABDD] text-xs font-black text-white">
        {n}
      </span>
      <span className="flex-1">{children}</span>
    </li>
  );
}

function CloseButton({ onClose, testId }) {
  return (
    <button
      type="button"
      onClick={onClose}
      data-testid={testId}
      className="mt-6 w-full rounded-md bg-[#0A0F17] py-3 font-display text-sm font-bold text-white transition-colors hover:bg-[#6CABDD] hover:text-[#0A0F17]"
    >
      Got it
    </button>
  );
}
